import { useState } from "react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Label } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { FranceMap } from "../components/FranceMap";
import { REGIONS } from "../data/regions";
import { MapPin } from "lucide-react";

const ZoneIntervention = () => {
  const [activeRegion, setActiveRegion] = useState("Occitanie");

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Revêtements de sols Resineko",
    provider: { "@type": "Organization", name: "Resineko" },
    areaServed: { "@type": "Country", name: "France" },
  };

  return (
    <>
      <Seo
        title="Resineko intervient partout en France | Zone d'intervention"
        description="Basée dans la région toulousaine, Resineko accompagne ses clients partout en France : résine, béton ciré, moquette de pierre et peinture. Toulouse, Bordeaux, Lyon, Marseille, Paris…"
        path="/zone-intervention"
        jsonLd={jsonLd}
      />
      <PageHero
        kicker="Zone d'intervention"
        title="Resineko intervient partout en France"
        subtitle="Basée dans la région toulousaine, Resineko accompagne ses clients partout en France pour leurs projets de revêtements de sols, résine, béton ciré, moquette de pierre et peinture."
        image="/images/hero-main.jpg"
        dim
      />

      <section className="bg-[#0A0A0A] py-20 md:py-32" data-testid="zone-map-section">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 md:grid-cols-2 md:px-10">
          <div>
            <Reveal><Label>La carte</Label></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] text-[#F4F4F0] md:text-5xl">
                Un ancrage toulousain, une vocation nationale
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-lg text-sm leading-relaxed text-[#8C8C8C] md:text-base">
                Survolez ou sélectionnez une région pour découvrir les principales villes où nous intervenons. Votre ville n'apparaît pas ? Contactez-nous : nous étudions chaque projet, où qu'il soit.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10">
                <FranceMap activeRegion={activeRegion} setActiveRegion={setActiveRegion} />
              </div>
            </Reveal>
          </div>

          <div className="space-y-4">
            {REGIONS.map((region, i) => (
              <Reveal key={region.name} delay={i * 0.05}>
                <button
                  onClick={() => setActiveRegion(region.name === activeRegion ? null : region.name)}
                  data-testid={`region-${region.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={`w-full border p-6 text-left transition-colors duration-300 md:p-7 ${
                    activeRegion === region.name
                      ? "border-[#047857] bg-[#047857]/10"
                      : "border-white/10 bg-[#111] hover:border-[#047857]/50"
                  }`}
                  aria-expanded={activeRegion === region.name}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-serif text-2xl font-light text-[#F4F4F0]">
                      {region.name}
                      {region.highlight && (
                        <span className="ml-3 align-middle text-[10px] uppercase tracking-[0.25em] text-[#2FBF8F]">Notre région</span>
                      )}
                    </h3>
                    <MapPin size={16} className={activeRegion === region.name ? "text-[#2FBF8F]" : "text-[#8C8C8C]"} />
                  </div>
                  <div className={`grid transition-[grid-template-rows,opacity] duration-500 ${activeRegion === region.name ? "mt-4 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                    <div className="overflow-hidden">
                      <p className="text-sm leading-relaxed text-[#8C8C8C]">{region.cities.join(" • ")}</p>
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#0e0e0e] py-16">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal><Label>Pages locales</Label></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-serif text-3xl font-light text-[#F4F4F0] md:text-4xl">
              Au plus près de chez vous
            </h2>
          </Reveal>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="/resine-sol-daux"
              data-testid="zone-link-daux"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Daux &amp; Toulouse Ouest
            </a>
            <a
              href="/resine-sol-toulouse"
              data-testid="zone-link-toulouse"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Toulouse
            </a>
            <a
              href="/resine-sol-bordeaux"
              data-testid="zone-link-bordeaux"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Bordeaux
            </a>
            <a
              href="/resine-sol-montpellier"
              data-testid="zone-link-montpellier"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Montpellier
            </a>
            <a
              href="/resine-sol-lyon"
              data-testid="zone-link-lyon"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Lyon
            </a>
            <a
              href="/resine-sol-nantes"
              data-testid="zone-link-nantes"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Nantes
            </a>
            <a
              href="/resine-sol-lille"
              data-testid="zone-link-lille"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Lille
            </a>
            <a
              href="/resine-sol-nice"
              data-testid="zone-link-nice"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Nice
            </a>
            <a
              href="/resine-sol-montauban"
              data-testid="zone-link-montauban"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Montauban
            </a>
            <a
              href="/resine-sol-blagnac"
              data-testid="zone-link-blagnac"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Blagnac
            </a>
            <a
              href="/resine-sol-colomiers"
              data-testid="zone-link-colomiers"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Colomiers
            </a>
            <a
              href="/resine-sol-tournefeuille"
              data-testid="zone-link-tournefeuille"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Tournefeuille
            </a>
            <a
              href="/resine-sol-muret"
              data-testid="zone-link-muret"
              className="group inline-flex items-center gap-2 border border-[#047857]/50 bg-[#047857]/10 px-5 py-3 text-sm text-[#F4F4F0] transition-colors duration-300 hover:border-[#2FBF8F]"
            >
              Sol en résine à Muret
            </a>
          </div>
        </div>
      </section>

      <CTASection
        title="Votre ville, votre projet, notre savoir-faire."
        text="Où que vous soyez en France, décrivez-nous votre projet : nous étudions chaque demande avec la même attention."
      />
    </>
  );
};

export default ZoneIntervention;
